import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import en from './en/translation.json';
import ptPT from './pt-PT/translation.json';

export const LANGUAGE_STORAGE_KEY = 'tailtrails_language';
export const SUPPORTED_LANGUAGES = ['en', 'pt-PT'] as const;
export const FALLBACK_LANGUAGE = 'en';
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Try to detect the device locale. If expo-localization's native module
// isn't available (e.g. needs a dev-client rebuild), fall back gracefully.
function mapDeviceLocaleToLanguage(): string {
  try {
    // Dynamic require so the app doesn't crash if the native module is missing
    const { getLocales } = require('expo-localization');
    const deviceLocales = getLocales();
    const deviceLanguage = deviceLocales[0]?.languageCode || 'en';
    const deviceRegion = deviceLocales[0]?.regionCode || '';

    // Exact match for pt-PT
    if (deviceLanguage === 'pt' && (deviceRegion === 'PT' || deviceRegion === '')) {
      return 'pt-PT';
    }
    // Any Portuguese maps to pt-PT (no Brazilian Portuguese variant yet)
    if (deviceLanguage === 'pt') {
      return 'pt-PT';
    }
  } catch (e) {
    // expo-localization native module not available — fall back to English.
    // This happens when running in an environment where the native module
    // hasn't been compiled (e.g. web, or a stale dev-client build).
    console.warn(
      '[i18n] expo-localization native module not available. ' +
      'Falling back to English. If you are on a native device, run: npx expo run:ios / npx expo run:android',
    );
  }
  return 'en';
}

// Synchronous initial language: try device locale immediately so there's no flash
const initialLanguage = mapDeviceLocaleToLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      'pt-PT': { translation: ptPT },
    },
    lng: initialLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    compatibilityJSON: 'v4',
    nsSeparator: false,
    keySeparator: '.',
  });

// After init, check AsyncStorage for a saved preference and switch if different.
// This runs in the background so there's at most one re-render if the user had
// a different language saved.
AsyncStorage.getItem(LANGUAGE_STORAGE_KEY).then((storedLanguage) => {
  if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage as any) && storedLanguage !== i18n.language) {
    i18n.changeLanguage(storedLanguage);
  }
}).catch(() => {
  // Ignore AsyncStorage errors
});

export function setLanguagePreference(language: string): Promise<void> {
  return AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, language).then(() => {
    return i18n.changeLanguage(language).then(() => {});
  });
}

export default i18n;